import { FC, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { BreadcrumbsNav } from '@rideiq/shared/components';
import { Button } from '@rideiq/shared/components/Button';
import { PageErrorFallback } from '@rideiq/shared/components/PageErrorFallback';
import { QueryErrorState } from '@rideiq/shared/components/QueryErrorState';
import { ErrorBoundary } from '@rideiq/utils/errorReporting/ErrorBoundary';

import { actions, Can, subjects } from '../../../contexts/AbilityContext';
import { useFlags } from '../../../contexts/LDContext';
import { ReactivateRider } from '../../components';
import { Contacts } from '../../features/Contacts';
import { DeactivateRider } from '../../features/DeactivateRider';
import PauseRider from '../../features/PauseRider';
import { RiderLocations } from '../../features/RiderLocations';
import { RiderProfile } from '../../features/RiderProfile';
import { RiderServicesReadOnlyCard } from '../../features/RiderServicesCard';
import { RidesList } from '../../features/RidesList';
import { SafetySettings } from '../../features/SafetySettings';
import UnpauseRider from '../../features/UnpauseRider';
import { useRiderMutations } from '../../hooks/useRiderMutations';
import { useRiderQuery } from '../../hooks/useRiderQuery';

import { CardsContainer, Container, Heading, RiderActions } from './styles';

const RiderCentricDetails: FC = () => {
  const { riderId } = useParams() as { riderId: string };
  const location = useLocation();
  const { riderDetails, fetchRiderBySlug, isFetchingRider, isErrorRider, refetchRider } = useRiderQuery();
  const { saveProfile, reactivateRider } = useRiderMutations(riderId);
  const { iepStructuredNotesEnabled } = useFlags();
  const useNewForm = iepStructuredNotesEnabled;
  const navigate = useNavigate();

  const [headingHeight, setHeadingHeight] = useState<number>(0);

  useEffect(() => {
    async function loadRiderDetails() {
      await fetchRiderBySlug(riderId);
    }
    riderId && loadRiderDetails();
  }, []);

  const getHeadingHeight = (nextHeadingHeight?: number) => {
    setHeadingHeight(nextHeadingHeight || 0);
  };

  return (
    <ErrorBoundary FallbackComponent={PageErrorFallback}>
      <Container>
        <Heading>
          <BreadcrumbsNav
            crumbs={[
              { title: 'All riders', pathname: '/riders' + (location?.state?.query ? `?${location.state.query}` : '') },
            ]}
          />

          {riderDetails ? (
            <RiderActions>
              <Can I={actions.edit} a={subjects.rider}>
                {useNewForm && (
                  <Button
                    data-testid="edit-rider-btn"
                    color="secondary"
                    size="large"
                    onClick={() => navigate(`/riders/${riderId}/edit`)}
                  >
                    Edit Rider
                  </Button>
                )}
                <UnpauseRider
                  slug={riderId}
                  state={riderDetails.state}
                  pausedAt={riderDetails?.pauseInfo?.pausedAt}
                  pausedUntil={riderDetails?.pauseInfo?.pausedUntil}
                />
                {!riderDetails.isAide && (
                  <PauseRider
                    slug={riderId}
                    state={riderDetails.state}
                    pausedAt={riderDetails?.pauseInfo?.pausedAt}
                    pauseDisabled={riderDetails?.pauseDisabled ?? false}
                    pauseDisabledReason={riderDetails?.pauseDisabledReason ?? ''}
                  />
                )}
                <ReactivateRider rider={riderDetails} reactivateRider={reactivateRider} />
                <DeactivateRider riderId={riderId} />
              </Can>
            </RiderActions>
          ) : null}
        </Heading>
        {isErrorRider ? (
          <QueryErrorState
            message="Something went wrong loading this rider. Please try again."
            tableHeightOffset={0}
            onRetry={refetchRider}
          />
        ) : (
          <>
            <RiderProfile
              isFetching={isFetchingRider}
              riderDetails={riderDetails}
              saveProfileEdit={saveProfile}
              getHeadingHeight={getHeadingHeight}
            />
            <CardsContainer>
              {useNewForm ? (
                <RiderServicesReadOnlyCard
                  isFetching={isFetchingRider}
                  codeWord={riderDetails?.codeWord ?? ''}
                  riderNotes={riderDetails?.riderNotes ?? ''}
                  supportSettings={(riderDetails?.supportSettings ?? []).map((s) => ({ ...s, selected: true }))}
                  services={riderDetails?.services}
                  boosterSeat={riderDetails?.boosterSeat}
                  frontSeatAuthorized={riderDetails?.frontSeatAuthorized}
                />
              ) : (
                <SafetySettings
                  isFetching={isFetchingRider}
                  id={riderId}
                  dateOfBirth={riderDetails?.dateOfBirth}
                  frontSeatAuthorized={riderDetails?.frontSeatAuthorized}
                  boosterSeat={riderDetails?.boosterSeat}
                  mustBeMet={riderDetails?.mustBeMet}
                  riderNotes={riderDetails?.riderNotes}
                  permanentRiderNote={riderDetails?.permanentRiderNote}
                  codeWord={riderDetails?.codeWord}
                  services={riderDetails?.services}
                />
              )}
              <RiderLocations riderSlug={riderId} isRiderActive={riderDetails?.state === 'active' || false} />
              <Contacts
                isFetching={isFetchingRider}
                riderSlug={riderId}
                riderContacts={riderDetails?.passengersContacts || []}
                riderFullName={`${riderDetails?.firstName} ${riderDetails?.lastName}`}
                updateContacts={saveProfile}
              />
            </CardsContainer>
            {riderDetails ? (
              <RidesList
                riderId={riderId}
                isSharedBillingRider={!!riderDetails.sharedBillingRider}
                riderName={[riderDetails.firstName, riderDetails.lastName].filter((name) => name).join(' ')}
                riderDetailsHeadingHeight={headingHeight}
                riderState={riderDetails.state}
              />
            ) : null}
          </>
        )}
        <Outlet />
      </Container>
    </ErrorBoundary>
  );
};

export default RiderCentricDetails;
